import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Helmet } from "react-helmet";
import { Layout } from 'antd';
import { Mail, Phone, MapPin, Send, MessageCircle, Clock, Globe, ArrowRight, CheckCircle, Linkedin, Twitter, Facebook } from 'lucide-react';
import axios from 'axios';
import Loader from '../components/Loader';

const Contact = () => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // WhatsApp Business Number
    const whatsappNumber = '+918420213444';

    // Contact Information
    const contactInfo = [
        {
            icon: Phone,
            title: "Phone",
            details: "+91 8420213444",
            href: "tel:+918420213444",
            color: "#48cfad"
        },
        {
            icon: Mail,
            title: "Email",
            details: "info@connectedbytes.in",
            href: "mailto:info@connectedbytes.in",
            color: "#ffcc66"
        },
        {
            icon: MapPin,
            title: "Office Address",
            details: "Kolkata, West Bengal, India",
            href: "https://maps.google.com/?q=Kolkata+West+Bengal+India",
            color: "#48cfad"
        },
    ];

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (submitError) setSubmitError('');
    };

    // Format message for WhatsApp
    const formatWhatsAppMessage = () => {
        return `*New Contact Form Submission*%0A%0A
*Name:* ${formData.name}%0A
*Email:* ${formData.email}%0A
*Phone:* ${formData.phone || 'Not provided'}%0A
*Subject:* ${formData.subject}%0A
*Message:* ${formData.message}%0A%0A
*Sent from:* ConnectedBytes Website Contact Form`;
    };

    // Handle form submission to WhatsApp
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.name || !formData.email || !formData.message) {
            setSubmitError('Please fill in all required fields (Name, Email, Message)');
            return;
        }

        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setSubmitError('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        try {
            // Format the message
            const whatsappMessage = formatWhatsAppMessage();

            // Create WhatsApp URL
            const whatsappURL = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${whatsappMessage}`;

            // Open WhatsApp in new window
            window.open(whatsappURL, '_blank');

            // Show success message
            setSubmitSuccess(true);

            // Reset form after 2 seconds
            setTimeout(() => {
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                });
                setSubmitSuccess(false);
            }, 3000);

        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitError('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Quick WhatsApp chat
    const openWhatsAppChat = () => {
        const message = `Hello! I'm interested in your services. Can you please provide more information?`;
        const whatsappURL = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodeURIComponent(message)}`;
        window.open(whatsappURL, '_blank');
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <Helmet>
                <title>Contact Us - ConnectedBytes</title>
                <meta name="description" content="Get in touch with ConnectedBytes for software development, digital transformation, and IT consulting services." />
                <meta property="og:title" content="Contact Us - ConnectedBytes" />
                <meta property="og:description" content="Let's discuss your project and how we can help bring your ideas to life." />
                <meta property="og:image" content="https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN" />
                <meta property="og:url" content="https://connectedbytes.in/contact" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>

            <div className="bg-background text-foreground min-h-screen transition-colors duration-300">
                <Layout className='bg-transparent'>
                    {/* Hero Section */}
                    <div className="relative pt-32 pb-24 overflow-hidden border-b border-border">
                        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/10 blur-[150px] rounded-full pointer-events-none" />
                        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-accent/10 blur-[150px] rounded-full pointer-events-none" />

                        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
                            <motion.div
                                initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                                transition={{ duration: 0.8, type: 'spring' }}
                            >
                                <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-4">Get In Touch</span>
                                <h1 className="text-5xl md:text-6xl lg:text-7xl font-black font-heading mb-6 tracking-tight">
                                    Contact Us
                                </h1>
                                <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full mb-8 shadow-[0_0_15px_rgba(var(--color-primary),0.5)]" />
                                <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-body leading-relaxed">
                                    Have a massively ambitious project in mind? We'd love to hear from you. Let's discuss how we can engineer an elite technical execution.
                                </p>
                            </motion.div>
                        </div>
                    </div>

                    {/* Contact Info Overview */}
                    <div className="py-10 relative overflow-hidden">
                        <div className="absolute top-1/2 left-0 w-64 h-64 bg-accent/5 rounded-full blur-[100px] -z-10" />
                        <div className="max-w-7xl mx-auto px-4 relative z-10">

                            {/* Contact Form and Map Section */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                                {/* Contact Form */}
                                <motion.div
                                    initial={{ opacity: 0, x: -50, filter: 'blur(10px)' }}
                                    whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                                    transition={{ duration: 0.8, type: 'spring' }}
                                    viewport={{ once: true }}
                                    className="bg-card/40 backdrop-blur-md rounded-3xl p-8 lg:p-10 shadow-sm border border-border relative overflow-hidden group"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                    <h2 className="text-3xl font-black font-heading mb-3 relative z-10">Send us a Message</h2>
                                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent rounded-full mb-6 relative z-10" />
                                    <p className="text-muted-foreground mb-8 font-body relative z-10">
                                        Initiate contact through our secure channel. Our system will prioritize your request immediately.
                                    </p>

                                    {submitSuccess && (
                                        <div className="mb-8 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3 relative z-10">
                                            <CheckCircle className="w-6 h-6 text-green-500" />
                                            <p className="text-green-600 font-medium">Transmission Successful. Our team will engage shortly.</p>
                                        </div>
                                    )}

                                    {submitError && (
                                        <div className="mb-8 p-4 bg-destructive/10 border border-destructive/30 rounded-xl relative z-10">
                                            <p className="text-destructive font-medium">{submitError}</p>
                                        </div>
                                    )}

                                    <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                                        <div>
                                            <label className="block tracking-wide text-foreground font-bold mb-2 font-secondary text-sm">
                                                Your Name <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className="w-full px-5 py-4 bg-background border border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-body text-foreground"
                                                placeholder="Enter full name"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block tracking-wide text-foreground font-bold mb-2 font-secondary text-sm">
                                                Email Address <span className="text-destructive">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full px-5 py-4 bg-background border border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-body text-foreground"
                                                placeholder="Enter corporate email"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block tracking-wide text-foreground font-bold mb-2 font-secondary text-sm">
                                                Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full px-5 py-4 bg-background border border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-body text-foreground"
                                                placeholder="Include country code"
                                            />
                                        </div>

                                        <div>
                                            <label className="block tracking-wide text-foreground font-bold mb-2 font-secondary text-sm">
                                                Subject
                                            </label>
                                            <input
                                                type="text"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleInputChange}
                                                className="w-full px-5 py-4 bg-background border border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all font-body text-foreground"
                                                placeholder="Brief overview"
                                            />
                                        </div>

                                        <div>
                                            <label className="block tracking-wide text-foreground font-bold mb-2 font-secondary text-sm">
                                                Message <span className="text-destructive">*</span>
                                            </label>
                                            <textarea
                                                name="message"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                rows="5"
                                                className="w-full px-5 py-4 bg-background border border-border rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-y font-body text-foreground"
                                                placeholder="Detail your requirements..."
                                                required
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-gradient-to-r from-primary to-accent text-primary-foreground font-bold py-4 px-8 rounded-xl shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.5)] transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-secondary text-lg mt-4"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="w-6 h-6 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                                                    Transmitting...
                                                </>
                                            ) : (
                                                <>
                                                    Deploy Message
                                                    <Send className="w-5 h-5" />
                                                </>
                                            )}
                                        </button>
                                    </form>
                                </motion.div>

                                {/* Map Section */}
                                <motion.div
                                    initial={{ opacity: 0, x: 50, filter: 'blur(10px)' }}
                                    whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                                    transition={{ duration: 0.8, type: 'spring' }}
                                    viewport={{ once: true }}
                                    className="bg-card/40 backdrop-blur-md rounded-3xl p-8 lg:p-10 shadow-sm border border-border relative overflow-hidden"
                                >
                                    <h2 className="text-3xl font-black font-heading mb-3 relative z-10">Find Us Here</h2>
                                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent rounded-full mb-6 relative z-10" />
                                    <p className="text-muted-foreground mb-8 font-body relative z-10">
                                        Locate our engineering hub instantly.
                                    </p>

                                    {/* Google Maps Embed */}
                                    <div className="rounded-2xl overflow-hidden shadow-lg mb-8 border border-border/50 bg-background relative z-10 relative group">
                                        <div className="absolute inset-0 bg-primary/20 mix-blend-overlay pointer-events-none opacity-50 group-hover:opacity-0 transition-opacity duration-700" />
                                        <iframe
                                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d235013.74843759175!2d88.30111839999999!3d22.6189262!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f882db4908b3a1%3A0x4c26b53b6f572a6!2sKolkata%2C%20West%20Bengal!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin"
                                            width="100%"
                                            height="280"
                                            style={{ border: 0, filter: "grayscale(20%) contrast(1.1)" }}
                                            allowFullScreen=""
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                            title="ConnectedBytes Location"
                                            className="w-full"
                                        />
                                    </div>

                                    {/* Additional Info with Buttons Below */}
                                    <div className="bg-background/80 backdrop-blur-sm rounded-2xl p-6 border border-border relative z-10">
                                        <h3 className="text-lg font-bold font-heading mb-3 flex items-center gap-2">
                                            <Clock className="w-5 h-5 text-primary" />
                                            Facility Hours
                                        </h3>
                                        <div className="space-y-2 font-body">
                                            <div className="flex justify-between border-b border-border/50 pb-2">
                                                <span className="text-muted-foreground text-sm">Monday - Saturday:</span>
                                                <span className="font-bold text-foreground text-sm">10:00 AM - 7:00 PM IST</span>
                                            </div>
                                            <div className="flex justify-between pb-2">
                                                <span className="text-muted-foreground text-sm">Sunday:</span>
                                                <span className="font-bold text-foreground opacity-50 text-sm">Fixed Off</span>
                                            </div>
                                        </div>

                                        {/* Call, Email, WhatsApp Buttons with Details - Mobile Responsive */}
                                        <div className="mt-5 pt-4 border-t border-border">
                                            <div className="grid grid-cols-1 gap-3">
                                                <a
                                                    href="tel:+918420213444"
                                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl bg-green-500/10 hover:bg-green-500/20 transition-all duration-300 group hover:scale-[1.02]"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Phone className="w-5 h-5 text-green-500 flex-shrink-0" />
                                                        <span className="text-sm font-medium text-foreground">Call Us</span>
                                                    </div>
                                                    <span className="text-sm text-green-600 font-mono break-all sm:break-normal">+91 8420213444</span>
                                                </a>
                                                <a
                                                    href="mailto:info@connectedbytes.in"
                                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 transition-all duration-300 group hover:scale-[1.02]"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Mail className="w-5 h-5 text-blue-500 flex-shrink-0" />
                                                        <span className="text-sm font-medium text-foreground">Email Us</span>
                                                    </div>
                                                    <span className="text-sm text-blue-600 font-mono break-all sm:break-normal">info@connectedbytes.in</span>
                                                </a>
                                                <a
                                                    href={`https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodeURIComponent("Hello! I'm interested in your services.")}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-all duration-300 group hover:scale-[1.02]"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <MessageCircle className="w-5 h-5 text-[#25D366] flex-shrink-0" />
                                                        <span className="text-sm font-medium text-foreground">WhatsApp</span>
                                                    </div>
                                                    <span className="text-sm text-[#25D366] font-mono break-all sm:break-normal">+91 8420213444</span>
                                                </a>
                                            </div>
                                            <p className="text-xs text-muted-foreground text-center mt-4">
                                                Prioritized response within <span className="text-primary font-bold">24 hours</span>
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </Layout>
            </div>
        </>
    )
}

export default Contact;